// src/components/auth/ProtectedRoute.jsx

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ROUTES } from '@/routes/AppRouter';

/**
 * Protege rotas privadas.
 *
 * Props:
 *  - adminOnly: restringe a rota a usuários com is_admin=true,
 *    redirecionando para /admin/login se não autenticado ou não admin.
 *  - requiredUserType: restringe por user_type específico.
 *  - requiredStatus: restringe por status do perfil.
 */
const ProtectedRoute = ({
  children,
  requiredUserType = null,
  requiredStatus = null,
  adminOnly = false,
}) => {
  const { isAuthenticated, isLoading, loading, userProfile } = useAuth();
  const location = useLocation();

  const resolving = isLoading ?? loading ?? false;

  // Aguarda a resolução da sessão — nunca redireciona antes disso
  if (resolving) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        {LoadingSpinner
          ? <LoadingSpinner size="lg" />
          : (
            <div
              className="animate-spin rounded-full h-12 w-12 border-b-2"
              style={{ borderColor: 'var(--primary-main, #0d7377)' }}
            />
          )
        }
      </div>
    );
  }

  // ── Rotas adminOnly ──────────────────────────────────────
  // Redireciona para /admin/login (não para /login) para não misturar
  // o fluxo de autenticação admin com o fluxo normal de usuários.
  if (adminOnly) {
    if (!isAuthenticated) {
      return <Navigate to={ROUTES.ADMIN_LOGIN} state={{ from: location }} replace />;
    }
    if (!userProfile?.is_admin) {
      return <Navigate to={ROUTES.ADMIN_LOGIN} replace />;
    }
    return children;
  }

  // ── Rotas normais ────────────────────────────────────────

  // Não autenticado: envia para login preservando destino original
  if (!isAuthenticated) {
    return (
      <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />
    );
  }

  // Tipo de usuário incorreto para esta rota específica
  if (requiredUserType && userProfile?.user_type !== requiredUserType) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // Status inválido para esta rota específica
  if (requiredStatus && userProfile?.status !== requiredStatus) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return children;
};

export default ProtectedRoute;
